import { ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  getPlatformSwitchPath,
  shouldShowPlatformSwitcher,
  type PlatformId,
} from '../utils/platformSwitch';

const PLATFORM_LABEL: Record<PlatformId, { switch: string; full: string }> = {
  ims: { switch: 'Go to IMS', full: 'Invoice Management System' },
  vms: { switch: 'Go to VMS', full: 'Vendor Management System' },
};

type PlatformSwitcherProps = {
  currentPlatform: PlatformId;
  variant?: 'vms-header' | 'ims-sidebar-inline';
  className?: string;
};

/**
 * Direct switch between IMS and VMS without logging out (dual-platform users only).
 */
export function PlatformSwitcher({
  currentPlatform,
  variant = 'vms-header',
  className = '',
}: PlatformSwitcherProps) {
  const { user } = useAuth();
  const navigate = useNavigate();

  const otherPlatform: PlatformId = currentPlatform === 'ims' ? 'vms' : 'ims';
  const other = PLATFORM_LABEL[otherPlatform];

  if (!shouldShowPlatformSwitcher(user)) {
    return null;
  }

  const handleSwitch = () => {
    navigate(getPlatformSwitchPath(otherPlatform, user), { replace: true });
  };

  return (
    <button
      type="button"
      className={[
        'platform-switcher',
        'platform-switcher__switch-btn',
        variant === 'vms-header'
          ? 'platform-switcher--vms-header'
          : 'platform-switcher--ims-sidebar-inline',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      onClick={handleSwitch}
      aria-label={`${other.switch} — ${other.full}`}
    >
      <span className="platform-switcher__switch-label">{other.switch}</span>
      <ArrowRight className="platform-switcher__switch-arrow" aria-hidden />
    </button>
  );
}
